import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck, LogOut, Inbox, Mail, KeyRound, Trash2, Plus, Loader2,
  RefreshCw, ExternalLink,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { adminApi, clearToken, apiError } from "@/lib/adminApi";

const STATUS = {
  new: { label: "New", cls: "bg-amber/15 text-amber ring-amber/40" },
  contacted: { label: "Contacted", cls: "bg-blue-500/15 text-blue-300 ring-blue-400/40" },
  closed: { label: "Closed", cls: "bg-white/10 text-muted-foreground ring-white/20" },
};

// Extracted from a nested ternary to keep the JSX readable and eslint-clean.
function renderBookingsTab({ loading, bookings, onChange }) {
  if (loading) {
    return (
      <div className="grid place-items-center py-20 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (bookings.length === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-white/12 py-20 text-center text-muted-foreground" data-testid="bookings-empty">
        No consultation requests yet.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="bookings-list">
      {bookings.map((b) => (
        <BookingRow key={b.id} b={b} onChange={onChange} />
      ))}
    </div>
  );
}

export default function AdminDashboard({ onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([adminApi.getBookings(), adminApi.getSettings()]);
      setBookings(b.data);
      setEmails(s.data.notify_emails || []);
    } catch (err) {
      if (err?.response?.status === 401) return onLogout();
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const counts = {
    total: bookings.length,
    new: bookings.filter((b) => b.status === "new").length,
    contacted: bookings.filter((b) => b.status === "contacted").length,
  };

  return (
    <div className="min-h-screen bg-ink text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex w-[min(1180px,94vw)] items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-amber/15 ring-1 ring-amber/40">
              <ShieldCheck className="h-4 w-4 text-amber" />
            </span>
            <span className="font-display text-xl">THS Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="hidden items-center gap-1.5 rounded-full border border-white/12 px-4 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground sm:flex" data-testid="admin-view-site">
              View site <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button onClick={() => { clearToken(); onLogout(); }} className="flex items-center gap-1.5 rounded-full bg-white/5 px-4 py-2 text-xs transition-colors hover:bg-white/10" data-testid="admin-logout-btn">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-[min(1180px,94vw)] py-10">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <p className="label-eyebrow text-amber">Dashboard</p>
            <h1 className="mt-3 font-display text-4xl leading-none">Consultation Requests</h1>
          </div>
          <button onClick={load} className="flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground" data-testid="admin-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="mb-8 grid grid-cols-3 gap-4">
          {[
            { k: "total", label: "Total requests", v: counts.total },
            { k: "new", label: "New / unactioned", v: counts.new },
            { k: "contacted", label: "In progress", v: counts.contacted },
          ].map((s) => (
            <div key={s.k} className="rounded-3xl border border-white/10 bg-ink-soft/70 p-6">
              <p className="font-display text-4xl text-amber">{s.v}</p>
              <p className="mt-2 text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="mb-6 grid w-full max-w-md grid-cols-3 bg-white/5">
            <TabsTrigger value="bookings" data-testid="tab-bookings"><Inbox className="mr-2 h-4 w-4" />Bookings</TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails"><Mail className="mr-2 h-4 w-4" />Emails</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security"><KeyRound className="mr-2 h-4 w-4" />Security</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            {renderBookingsTab({ loading, bookings, onChange: load })}
          </TabsContent>

          <TabsContent value="emails">
            <NotifyEmails emails={emails} setEmails={setEmails} />
          </TabsContent>

          <TabsContent value="security">
            <ChangePassword onDone={() => { clearToken(); onLogout(); }} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const BookingRow = ({ b, onChange }) => {
  const [busy, setBusy] = useState(false);
  const s = STATUS[b.status] || STATUS.new;
  const date = new Date(b.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  const setStatus = async (status) => {
    setBusy(true);
    try { await adminApi.updateBooking(b.id, status); await onChange(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this request?")) return;
    setBusy(true);
    try { await adminApi.deleteBooking(b.id); toast.success("Request deleted"); await onChange(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl border border-white/10 bg-ink-soft/70 p-5 md:p-6"
      data-testid={`booking-row-${b.id}`}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-base font-semibold">{b.name}</h3>
            <span className={`rounded-full px-3 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider ring-1 ${s.cls}`}>{s.label}</span>
            <span className="text-xs text-muted-foreground">{date}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
            <a href={`mailto:${b.email}`} className="text-amber hover:underline">{b.email}</a>
            {b.phone && <span>{b.phone}</span>}
            {b.company && <span>{b.company}</span>}
          </div>
          <p className="mt-3 text-sm text-foreground/90">{b.service} · <span className="text-muted-foreground">{b.site_type}</span>{b.employees ? ` · ${b.employees} staff` : ""}</p>
          {b.message && <p className="mt-2 rounded-2xl border border-white/8 bg-white/5 p-3 text-sm text-muted-foreground">{b.message}</p>}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <select
            value={b.status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={busy}
            data-testid={`booking-status-${b.id}`}
            className="rounded-xl border border-white/12 bg-white/5 px-3 py-2 text-xs outline-none focus:border-amber/60"
          >
            <option value="new" className="bg-ink-soft">New</option>
            <option value="contacted" className="bg-ink-soft">Contacted</option>
            <option value="closed" className="bg-ink-soft">Closed</option>
          </select>
          <button onClick={del} disabled={busy} className="grid h-9 w-9 place-items-center rounded-xl bg-red-500/10 text-red-400 transition-colors hover:bg-red-500/20" data-testid={`booking-delete-${b.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const NotifyEmails = ({ emails, setEmails }) => {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    const parsed = [...new Set(
      input.split(/[\s,;\n]+/).map((s) => s.trim().toLowerCase()).filter(Boolean)
    )];
    const valid = parsed.filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
    if (valid.length === 0) {
      toast.error("Enter one or more valid email addresses");
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi.addEmails(valid);
      setEmails(data.notify_emails);
      setInput("");
      const invalid = parsed.length - valid.length;
      toast.success(`${valid.length} destination${valid.length !== 1 ? "s" : ""} saved${invalid ? ` · ${invalid} skipped` : ""}`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const remove = async (addr) => {
    setBusy(true);
    try { const { data } = await adminApi.removeEmail(addr); setEmails(data.notify_emails); toast.success("Removed"); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl rounded-3xl border border-white/10 bg-ink-soft/70 p-6 md:p-8">
      <h3 className="font-display text-2xl">Notification destinations</h3>
      <p className="mt-2 text-sm text-muted-foreground">Every new consultation request is emailed to all addresses below. Add several at once — separate them with commas, spaces or new lines.</p>

      <form onSubmit={add} className="mt-6 space-y-3">
        <textarea
          value={input} onChange={(e) => setInput(e.target.value)} rows={2}
          placeholder="team@company.com, safety@company.com&#10;another@company.com"
          data-testid="notify-email-input"
          className="w-full resize-none rounded-2xl border border-white/12 bg-white/5 px-5 py-3.5 text-sm outline-none focus:border-amber/60"
        />
        <button type="submit" disabled={busy} data-testid="notify-email-add" className="flex items-center gap-2 rounded-2xl bg-amber px-5 py-3 text-sm font-semibold text-ink transition-colors hover:bg-gold disabled:opacity-60">
          <Plus className="h-4 w-4" /> Add destinations
        </button>
      </form>

      <div className="mt-6 space-y-2" data-testid="notify-email-list">
        {emails.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-white/12 py-8 text-center text-sm text-muted-foreground">
            No destinations yet — booking emails won't be sent until you add one.
          </p>
        ) : (
          emails.map((addr) => (
            <div key={addr} className="flex items-center justify-between rounded-2xl border border-white/8 bg-white/5 px-4 py-3">
              <span className="flex items-center gap-3 text-sm"><Mail className="h-4 w-4 text-amber" />{addr}</span>
              <button onClick={() => remove(addr)} className="text-muted-foreground transition-colors hover:text-red-400" data-testid={`notify-email-remove-${addr}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const ChangePassword = ({ onDone }) => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 10) return toast.error("New password must be at least 10 characters");
    setBusy(true);
    try {
      await adminApi.changePassword(cur, nw);
      toast.success("Password updated — please sign in again with your new password");
      setCur(""); setNw("");
      setTimeout(() => onDone && onDone(), 1200);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const inp = "w-full rounded-2xl border border-white/12 bg-white/5 px-5 py-3.5 text-sm outline-none focus:border-amber/60";
  return (
    <form onSubmit={submit} className="max-w-md rounded-3xl border border-white/10 bg-ink-soft/70 p-6 md:p-8">
      <h3 className="font-display text-2xl">Change password</h3>
      <p className="mt-2 text-sm text-muted-foreground">Update the admin dashboard password. Use at least 10 characters — you'll be signed out and asked to log in again.</p>
      <div className="mt-6 space-y-4">
        <label className="block">
          <span className="mb-2 block text-xs uppercase tracking-wider text-muted-foreground">Current password</span>
          <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className={inp} data-testid="current-password-input" />
        </label>
        <label className="block">
          <span className="mb-2 block text-xs uppercase tracking-wider text-muted-foreground">New password</span>
          <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} className={inp} data-testid="new-password-input" />
        </label>
        <button type="submit" disabled={busy} data-testid="change-password-btn" className="flex items-center justify-center gap-2 rounded-full bg-amber px-6 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-gold disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
        </button>
      </div>
    </form>
  );
};
